import React from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { brainNodeView } from './brainTreeModel';

const ROLE_TONES = { Heart: 'heart', Brain: 'brain', Driver: 'driver' };

/**
 * One node box (E1 §1.3.6). A focusable `role="button"` div — the + inside it
 * is a real button, so the box itself can't be one.
 */
function BrainNode({ node, box, selected, canManage, testing, childCount, onSelect, onAddChild }) {
    const view = brainNodeView(node, { canManage, testing, childCount });
    const classes = [
        'wk-brain-node',
        `wk-brain-node--${view.variant}`,
        view.groupDraft ? 'is-draft-group' : '',
        view.variant === 'normal' && view.weakBorder ? 'is-weak' : '',
        view.off ? 'is-off' : '',
        selected ? 'is-selected' : '',
    ].filter(Boolean).join(' ');
    const select = () => onSelect(node.id);
    return (
        <div
            className={classes}
            style={{ left: box.x, top: box.y, width: box.w }}
            role="button"
            tabIndex={0}
            title={node.label}
            aria-pressed={selected}
            data-testid={`brain-node-${node.id}`}
            data-variant={view.variant}
            onClick={select}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    select();
                }
            }}
        >
            {view.isCheck && (
                <span
                    className={`wk-brain-dot wk-brain-dot--${view.dotTone}`}
                    style={{ width: view.dotSize, height: view.dotSize, flexBasis: view.dotSize }}
                    aria-hidden="true"
                />
            )}
            {view.when && <span className="wk-brain-node-when">{`if ${view.when}`}</span>}
            <span className="wk-brain-node-label">{node.label}</span>
            {view.role && (
                <span className={`wk-brain-rolechip wk-brain-rolechip--${ROLE_TONES[view.role] || 'other'}`}>{view.role}</span>
            )}
            {view.childCount > 0 && <span className="wk-brain-node-count">{view.childCount}</span>}
            {view.learned && <WikiIcon name="sparkle" size={12} className="wk-brain-ic-learned" title="Learned" />}
            {view.admin && <WikiIcon name="pencil" size={11} className="wk-brain-ic-admin" title="Written by an admin" />}
            {view.fixed && <WikiIcon name="lock" size={11} className="wk-brain-ic-fixed" title="Fixed" />}
            {view.weak && <WikiIcon name="alert-triangle" size={12} className="wk-brain-ic-weak" title="Weak" />}
            {view.pendingVersion && <span className="wk-brain-node-pending">{view.pendingVersion}</span>}
            {view.statusChip && (
                <span className={`wk-brain-node-status wk-brain-node-status--${view.statusChip}`}>{view.statusChip}</span>
            )}
            {view.canChild && (
                <button
                    type="button"
                    className="wk-brain-node-plus"
                    title="Add a child check"
                    aria-label={`Add a child check under ${node.label}`}
                    data-testid={`brain-node-plus-${node.id}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onAddChild(node.id);
                    }}
                >
                    <WikiIcon name="plus" size={13} />
                </button>
            )}
        </div>
    );
}

/**
 * The tree canvas (E1 §1.3.4–§1.3.6): an absolutely positioned layout from
 * `layoutBrainTree`, bezier edges in one SVG, nodes on top. Scrolls inside its
 * card (`.wk-brain-canvas-card`).
 */
export default function BrainTreeCanvas({ model, selectedId, canManage, testingId, onSelect, onAddChild }) {
    const { layout, branchNodes, childCount } = model;
    const { width, height, pos, edges } = layout;
    return (
        <div className="wk-card wk-card--flush wk-brain-canvas-card" data-testid="brain-canvas-card">
            <div className="wk-brain-canvas-pad">
                <div
                    className="wk-brain-canvas"
                    style={{ width, height }}
                    data-testid="brain-canvas"
                    data-width={width}
                    data-height={height}
                >
                    <svg
                        className="wk-brain-edges"
                        width={width}
                        height={height}
                        viewBox={`0 0 ${width} ${height}`}
                        aria-hidden="true"
                    >
                        {edges.map((edge) => (
                            <path
                                key={edge.id}
                                d={edge.d}
                                className={`wk-brain-edge wk-brain-edge--${edge.tone}${edge.dashed ? ' is-dashed' : ''}`}
                                data-edge={`${edge.parentId}>${edge.id}`}
                            />
                        ))}
                    </svg>
                    {branchNodes.filter((node) => pos[node.id]).map((node) => (
                        <BrainNode
                            key={node.id}
                            node={node}
                            box={pos[node.id]}
                            selected={selectedId === node.id}
                            canManage={canManage}
                            testing={testingId === node.id}
                            childCount={childCount[node.id] || 0}
                            onSelect={onSelect}
                            onAddChild={onAddChild}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
